import { useState, useRef, useEffect, useCallback } from 'react';
import html2canvas from 'html2canvas';
import PrintReadyModal from './PrintReadyModal';
import FloorPlanSVG from './FloorPlanSVG';
import MaterialPalette, { DEFAULT_MATERIALS, type MaterialSelection } from './MaterialPalette';
import ImageWithSkeleton from '@/components/base/ImageWithSkeleton';
import FavoriteRoomsPanel from './FavoriteRoomsPanel';
import RoomCompareModal from './RoomCompareModal';

interface DesignCanvasProps {
  isGenerating: boolean;
  progress: number;
  currentStep: string;
  hasGenerated: boolean;
  selectedStyle: string;
  floorPlanConfig: any;
  darkMode: boolean;
  onRegenerate?: () => void;
  onShareDesign?: () => void;
  onApplyStyle?: (style: string) => void;
  onShowShortcuts?: () => void;
  onGenerate?: () => void;
}

const ARCHITECTURAL_STYLES = [
  'Colonial', 'Victorian', 'Craftsman', 'Tudor', 'Minimalist', 'Contemporary',
  'Industrial', 'Scandinavian', 'Mediterranean', 'Farmhouse', 'Ranch',
  'Cape Cod', 'Tiny Home', 'Container', 'A-Frame', 'Dome'
];

const TIPS = [
  'Use the Floor Plan tab to configure bedrooms, bathrooms, and extras before generating.',
  'Select a House Style first to get the most accurate 3D render.',
  'Try the AI Chat tab to ask questions about your design in natural language.',
  'Use the Cost Estimator to get a budget breakdown before finalizing your plan.',
  'Save your project often — use Ctrl+S for a quick save.',
  'The Compare Styles feature in the Style tab lets you view two styles side by side.',
  'Add a Design Prompt in the Floor Plan tab for more personalized results.',
  'Use the Space Planner to drag and drop furniture into your rooms.',
  'The AI Render Studio can generate photorealistic exterior images of your design.',
  'Share your design with clients using the Share button in the top navigation.',
];

const FEATURE_HIGHLIGHTS = [
  { icon: 'ri-layout-grid-line', label: 'Floor Plans', color: 'text-teal-500', bg: 'bg-teal-500/10' },
  { icon: 'ri-building-2-line', label: '3D Renders', color: 'text-indigo-500', bg: 'bg-indigo-500/10' },
  { icon: 'ri-walk-line', label: 'Walkthroughs', color: 'text-amber-500', bg: 'bg-amber-500/10' },
  { icon: 'ri-palette-line', label: 'Color Palettes', color: 'text-rose-500', bg: 'bg-rose-500/10' },
  { icon: 'ri-money-dollar-circle-line', label: 'Cost Estimator', color: 'text-green-500', bg: 'bg-green-500/10' },
  { icon: 'ri-robot-line', label: 'AI Chat', color: 'text-cyan-500', bg: 'bg-cyan-500/10' },
];

const INSPIRATION_GALLERY = [
  {
    style: 'Minimalist',
    label: 'Clean & Modern',
    image: 'https://public.readdy.ai/ai/img_res/c038bb04cad6594eb8b2bd3c1ca986dc.jpg',
    accent: 'from-slate-500 to-gray-600',
    tag: 'Popular',
  },
  {
    style: 'Mediterranean',
    label: 'Warm & Earthy',
    image: 'https://readdy.ai/api/search-image?query=mediterranean%20villa%20with%20terracotta%20roof%20stucco%20walls%20arched%20windows%20courtyard%20warm%20earth%20tones%20beautiful%20landscaping%20palm%20trees%20clear%20sky&width=600&height=400&seq=insp-mediterranean-001&orientation=landscape',
    accent: 'from-orange-500 to-amber-600',
    tag: 'Trending',
  },
  {
    style: 'Scandinavian',
    label: 'Nordic Simplicity',
    image: 'https://readdy.ai/api/search-image?query=scandinavian%20style%20house%20with%20light%20wood%20facade%20simple%20clean%20design%20large%20windows%20natural%20materials%20nordic%20architecture%20beautiful%20minimal%20garden&width=600&height=400&seq=insp-scandinavian-001&orientation=landscape',
    accent: 'from-sky-500 to-cyan-600',
    tag: 'Cozy',
  },
  {
    style: 'Farmhouse',
    label: 'Rustic Charm',
    image: 'https://readdy.ai/api/search-image?query=modern%20farmhouse%20with%20white%20board%20and%20batten%20siding%20black%20windows%20metal%20roof%20front%20porch%20rustic%20charm%20beautiful%20landscaping%20clear%20sky&width=600&height=400&seq=insp-farmhouse-001&orientation=landscape',
    accent: 'from-stone-500 to-neutral-600',
    tag: 'Classic',
  },
  {
    style: 'A-Frame',
    label: 'Mountain Retreat',
    image: 'https://readdy.ai/api/search-image?query=a-frame%20cabin%20with%20steep%20triangular%20roof%20large%20front%20windows%20wood%20siding%20mountain%20retreat%20architecture%20beautiful%20natural%20landscaping%20clear%20sky&width=600&height=400&seq=insp-aframe-001&orientation=landscape',
    accent: 'from-emerald-500 to-teal-600',
    tag: 'Unique',
  },
  {
    style: 'Contemporary',
    label: 'Bold & Innovative',
    image: 'https://readdy.ai/api/search-image?query=contemporary%20house%20with%20mixed%20materials%20glass%20steel%20wood%20asymmetrical%20design%20innovative%20architecture%20beautiful%20modern%20landscaping%20clear%20blue%20sky&width=600&height=400&seq=insp-contemporary-001&orientation=landscape',
    accent: 'from-violet-500 to-fuchsia-600',
    tag: 'Award-winning',
  },
];

const STYLE_PREVIEW_IMAGES: Record<string, string> = {
  Colonial: 'https://readdy.ai/api/search-image?query=elegant%20colonial%20style%20house%20with%20white%20columns%20symmetrical%20facade%20brick%20exterior%20traditional%20american%20architecture%20beautiful%20landscaped%20front%20yard%20clear%20blue%20sky&width=800&height=500&seq=prev-colonial-001&orientation=landscape',
  Victorian: 'https://readdy.ai/api/search-image?query=ornate%20victorian%20house%20with%20decorative%20trim%20bay%20windows%20turret%20tower%20pastel%20colors%20intricate%20architectural%20details%20beautiful%20garden%20clear%20sky&width=800&height=500&seq=prev-victorian-001&orientation=landscape',
  Craftsman: 'https://readdy.ai/api/search-image?query=craftsman%20style%20bungalow%20with%20exposed%20beams%20wide%20front%20porch%20natural%20wood%20siding%20stone%20accents%20warm%20inviting%20architecture%20beautiful%20landscaping&width=800&height=500&seq=prev-craftsman-001&orientation=landscape',
  Tudor: 'https://readdy.ai/api/search-image?query=tudor%20style%20house%20with%20half%20timbered%20facade%20steeply%20pitched%20roof%20decorative%20woodwork%20brick%20and%20stucco%20exterior%20charming%20english%20architecture%20beautiful%20garden&width=800&height=500&seq=prev-tudor-001&orientation=landscape',
  Minimalist: 'https://readdy.ai/api/search-image?query=minimalist%20modern%20house%20with%20clean%20lines%20white%20walls%20large%20windows%20simple%20geometric%20shapes%20sleek%20contemporary%20architecture%20beautiful%20minimal%20landscaping%20clear%20sky&width=800&height=500&seq=prev-minimalist-001&orientation=landscape',
  Contemporary: 'https://readdy.ai/api/search-image?query=contemporary%20house%20with%20mixed%20materials%20glass%20steel%20wood%20asymmetrical%20design%20innovative%20architecture%20beautiful%20modern%20landscaping%20clear%20blue%20sky&width=800&height=500&seq=prev-contemporary-001&orientation=landscape',
  Industrial: 'https://readdy.ai/api/search-image?query=industrial%20style%20house%20with%20exposed%20concrete%20metal%20accents%20large%20windows%20raw%20materials%20urban%20architecture%20beautiful%20minimal%20landscaping&width=800&height=500&seq=prev-industrial-001&orientation=landscape',
  Scandinavian: 'https://readdy.ai/api/search-image?query=scandinavian%20style%20house%20with%20light%20wood%20facade%20simple%20clean%20design%20large%20windows%20natural%20materials%20nordic%20architecture%20beautiful%20minimal%20garden&width=800&height=500&seq=prev-scandinavian-001&orientation=landscape',
  Mediterranean: 'https://readdy.ai/api/search-image?query=mediterranean%20villa%20with%20terracotta%20roof%20stucco%20walls%20arched%20windows%20courtyard%20warm%20earth%20tones%20beautiful%20landscaping%20palm%20trees%20clear%20sky&width=800&height=500&seq=prev-mediterranean-001&orientation=landscape',
  Farmhouse: 'https://readdy.ai/api/search-image?query=modern%20farmhouse%20with%20white%20board%20and%20batten%20siding%20black%20windows%20metal%20roof%20front%20porch%20rustic%20charm%20beautiful%20landscaping%20clear%20sky&width=800&height=500&seq=prev-farmhouse-001&orientation=landscape',
  Ranch: 'https://readdy.ai/api/search-image?query=ranch%20style%20house%20with%20long%20low%20profile%20attached%20garage%20horizontal%20lines%20simple%20design%20beautiful%20landscaping%20wide%20open%20yard%20clear%20sky&width=800&height=500&seq=prev-ranch-001&orientation=landscape',
  'Cape Cod': 'https://readdy.ai/api/search-image?query=cape%20cod%20style%20house%20with%20steep%20roof%20dormer%20windows%20shingle%20siding%20symmetrical%20facade%20traditional%20new%20england%20architecture%20beautiful%20garden&width=800&height=500&seq=prev-capecod-001&orientation=landscape',
  'Tiny Home': 'https://readdy.ai/api/search-image?query=modern%20tiny%20house%20with%20efficient%20design%20large%20windows%20wood%20siding%20compact%20architecture%20beautiful%20minimal%20landscaping%20clear%20sky%20sustainable%20living&width=800&height=500&seq=prev-tinyhome-001&orientation=landscape',
  Container: 'https://readdy.ai/api/search-image?query=shipping%20container%20house%20with%20industrial%20design%20large%20windows%20modern%20conversion%20innovative%20architecture%20beautiful%20minimal%20landscaping%20clear%20sky&width=800&height=500&seq=prev-container-001&orientation=landscape',
  'A-Frame': 'https://readdy.ai/api/search-image?query=a-frame%20cabin%20with%20steep%20triangular%20roof%20large%20front%20windows%20wood%20siding%20mountain%20retreat%20architecture%20beautiful%20natural%20landscaping%20clear%20sky&width=800&height=500&seq=prev-aframe-001&orientation=landscape',
  Dome: 'https://readdy.ai/api/search-image?query=geodesic%20dome%20house%20with%20unique%20spherical%20design%20modern%20materials%20innovative%20architecture%20beautiful%20landscaping%20clear%20sky%20futuristic%20sustainable%20home&width=800&height=500&seq=prev-dome-001&orientation=landscape',
};

const SHORTCUTS_DATA = [
  {
    category: 'Navigation',
    icon: 'ri-compass-3-line',
    color: 'text-teal-500',
    keys: [
      { keys: ['Tab'], desc: 'Switch between panel tabs' },
      { keys: ['Esc'], desc: 'Close any open modal' },
    ],
  },
  {
    category: 'Projects',
    icon: 'ri-folder-line',
    color: 'text-violet-500',
    keys: [
      { keys: ['Ctrl', 'S'], desc: 'Open Save Project dialog' },
      { keys: ['Ctrl', 'O'], desc: 'Open My Projects panel' },
    ],
  },
  {
    category: 'View',
    icon: 'ri-eye-line',
    color: 'text-amber-500',
    keys: [
      { keys: ['Ctrl', 'D'], desc: 'Toggle Dark / Light mode' },
      { keys: ['Ctrl', 'Shift', 'F'], desc: 'Toggle fullscreen canvas' },
    ],
  },
];

// Different aerial camera angles / times of day for regeneration
const AERIAL_VARIANTS = [
  { label: 'Golden Hour', suffix: 'warm golden hour sunset lighting aerial view', seq: 'aerial-golden' },
  { label: 'Midday', suffix: 'bright midday clear blue sky aerial view overhead', seq: 'aerial-midday' },
  { label: 'Dusk', suffix: 'dusk twilight blue hour soft ambient lighting aerial view', seq: 'aerial-dusk' },
  { label: 'Overcast', suffix: 'overcast soft diffused light dramatic clouds aerial view', seq: 'aerial-overcast' },
  { label: 'Bird\'s Eye', suffix: 'straight down bird\'s eye view top-down overhead aerial', seq: 'aerial-birdseye' },
  { label: 'Isometric', suffix: '45 degree isometric angle aerial view architectural visualization', seq: 'aerial-iso' },
  { label: 'Night', suffix: 'night time exterior lights glowing warm windows aerial view', seq: 'aerial-night' },
  { label: 'Spring', suffix: 'spring season blooming trees green lawn aerial view', seq: 'aerial-spring' },
];

const VIEW_MODES = [
  { id: 'split', label: 'Design Visuals', icon: 'ri-layout-column-line' },
  { id: '2d', label: '2D Floor Plan', icon: 'ri-layout-grid-line' },
  { id: '3d', label: '3D Exterior', icon: 'ri-building-2-line' },
  { id: 'interior', label: 'Interior View', icon: 'ri-home-smile-line' },
  { id: '360', label: '360° Walkthrough', icon: 'ri-camera-3-line' },
];

// Style premium multipliers for cost calculation
const STYLE_PREMIUMS: Record<string, number> = {
  Colonial: 0.05, Victorian: 0.12, Craftsman: 0.08, Tudor: 0.15,
  Minimalist: 0.03, Contemporary: 0.10, Industrial: 0.07, Scandinavian: 0.06,
  Mediterranean: 0.14, Farmhouse: 0.04, Ranch: 0.02, 'Cape Cod': 0.05,
  'Tiny Home': -0.10, Container: -0.05, 'A-Frame': 0.08, Dome: 0.20,
};

const EXTRAS_COST: Record<string, number> = {
  Garage: 25000, Pool: 45000, Basement: 35000, Attic: 15000,
  'Home Office': 12000, Gym: 18000, 'Home Theater': 22000, Sauna: 14000,
};

function getStyleImage(style: string, viewMode: string, seq: string) {
  const exterior3D: Record<string, string> = {
    Colonial: 'elegant colonial style house with white columns symmetrical facade brick exterior traditional american architecture beautiful landscaped front yard clear blue sky photorealistic architectural render',
    Victorian: 'ornate victorian house with decorative trim bay windows turret tower pastel colors intricate architectural details beautiful garden clear sky photorealistic render',
    Craftsman: 'craftsman style bungalow with exposed beams wide front porch natural wood siding stone accents warm inviting architecture beautiful landscaping photorealistic render',
    Tudor: 'tudor style house with half timbered facade steeply pitched roof decorative woodwork brick and stucco exterior charming english architecture beautiful garden photorealistic',
    Minimalist: 'minimalist modern house with clean lines white walls large floor to ceiling windows simple geometric shapes sleek contemporary architecture beautiful minimal landscaping clear sky photorealistic render',
    Contemporary: 'contemporary house with mixed materials glass steel wood asymmetrical design innovative architecture beautiful modern landscaping clear blue sky photorealistic architectural render',
    Industrial: 'industrial style house with exposed concrete metal accents large windows raw materials urban architecture beautiful minimal landscaping photorealistic render',
    Scandinavian: 'scandinavian style house with light wood facade simple clean design large windows natural materials nordic architecture beautiful minimal garden photorealistic render',
    Mediterranean: 'mediterranean villa with terracotta roof stucco walls arched windows courtyard warm earth tones beautiful landscaping palm trees clear sky photorealistic render',
    Farmhouse: 'modern farmhouse with white board and batten siding black windows metal roof front porch rustic charm beautiful landscaping clear sky photorealistic render',
    Ranch: 'ranch style house with long low profile attached garage horizontal lines simple design beautiful landscaping wide open yard clear sky photorealistic render',
    'Cape Cod': 'cape cod style house with steep roof dormer windows shingle siding symmetrical facade traditional new england architecture beautiful garden photorealistic render',
    'Tiny Home': 'modern tiny house with efficient design large windows wood siding compact architecture beautiful minimal landscaping clear sky sustainable living photorealistic render',
    Container: 'shipping container house with industrial design large windows modern conversion innovative architecture beautiful minimal landscaping clear sky photorealistic render',
    'A-Frame': 'a-frame cabin with steep triangular roof large front windows wood siding mountain retreat architecture beautiful natural landscaping clear sky photorealistic render',
    Dome: 'geodesic dome house with unique spherical design modern materials innovative architecture beautiful landscaping clear sky futuristic sustainable home photorealistic render',
  };

  const interior: Record<string, string> = {
    Colonial: 'elegant colonial interior living room with crown molding wainscoting traditional furniture warm wood floors fireplace natural light photorealistic interior design render',
    Victorian: 'ornate victorian interior living room with high ceilings decorative moldings antique furniture rich colors stained glass windows photorealistic interior render',
    Craftsman: 'craftsman interior living room with exposed wood beams built in bookshelves natural materials warm tones cozy fireplace photorealistic interior design render',
    Minimalist: 'minimalist interior living room with clean lines white walls minimal furniture natural light open space concrete floors photorealistic interior design render',
    Contemporary: 'contemporary interior living room with open plan design modern furniture statement lighting mixed materials natural light photorealistic interior render',
    Scandinavian: 'scandinavian interior living room with light wood floors white walls minimal furniture cozy textiles natural light hygge atmosphere photorealistic render',
    Mediterranean: 'mediterranean interior living room with terracotta tiles arched doorways warm colors decorative tiles natural light photorealistic interior design render',
    Farmhouse: 'modern farmhouse interior living room with shiplap walls barn wood accents cozy furniture neutral tones natural light photorealistic interior render',
    Industrial: 'industrial interior living room with exposed brick concrete floors metal accents open ceiling Edison bulbs urban loft photorealistic render',
    Tudor: 'tudor interior living room with dark wood paneling stone fireplace leaded glass windows rich tapestries traditional furniture photorealistic render',
  };

  const panoramic: Record<string, string> = {
    Colonial: 'panoramic aerial view colonial style house with white columns brick exterior beautiful landscaped yard trees driveway clear sky photorealistic',
    Minimalist: 'panoramic aerial view minimalist modern house with clean lines white walls large windows beautiful minimal landscaping clear sky photorealistic',
    Contemporary: 'panoramic aerial view contemporary house with mixed materials glass steel beautiful modern landscaping pool clear blue sky photorealistic',
    Farmhouse: 'panoramic aerial view modern farmhouse with white siding metal roof front porch beautiful landscaping clear sky photorealistic',
    Mediterranean: 'panoramic aerial view mediterranean villa with terracotta roof courtyard pool beautiful landscaping palm trees clear sky photorealistic',
  };

  if (viewMode === '3d') {
    const q = exterior3D[style] || `${style} style house exterior photorealistic architectural render beautiful landscaping clear sky`;
    return `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28q%29%7D&width=1200&height=700&seq=${seq}&orientation=landscape`;
  }
  if (viewMode === 'interior') {
    const q = interior[style] || `beautiful ${style} style interior living room natural light elegant furniture photorealistic architectural interior render`;
    return `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28q%29%7D&width=1200&height=700&seq=${seq}&orientation=landscape`;
  }
  if (viewMode === '360') {
    const q = panoramic[style] || `panoramic aerial view beautiful ${style} style house exterior surrounding landscape clear sky photorealistic architectural visualization`;
    return `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28q%29%7D&width=1200&height=700&seq=${seq}&orientation=landscape`;
  }
  // 2D — SVG is rendered instead, but keep a fallback
  return `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28%60architectural%20blueprint%20floor%20plan%202D%20top%20view%20detailed%20room%20layout%20grid%20lines%20measurements%20professional%20design%20white%20background%20$%7Bstyle%7D%20style%60%29%7D&width=1200&height=700&seq=${seq}&orientation=landscape`;
}

function getStyleThumbnail(styleName: string) {
  const map: Record<string, string> = {
    Colonial: 'https://readdy.ai/api/search-image?query=elegant%20colonial%20style%20house%20with%20white%20columns%20symmetrical%20facade%20brick%20exterior%20traditional%20american%20architecture%20beautiful%20landscaped%20front%20yard%20clear%20blue%20sky&width=400&height=300&seq=colonial-style-001&orientation=landscape',
    Victorian: 'https://readdy.ai/api/search-image?query=ornate%20victorian%20house%20with%20decorative%20trim%20bay%20windows%20turret%20tower%20pastel%20colors%20intricate%20architectural%20details%20beautiful%20garden%20clear%20sky&width=400&height=300&seq=victorian-style-001&orientation=landscape',
    Craftsman: 'https://readdy.ai/api/search-image?query=craftsman%20style%20bungalow%20with%20exposed%20beams%20wide%20front%20porch%20natural%20wood%20siding%20stone%20accents%20warm%20inviting%20architecture%20beautiful%20landscaping&width=400&height=300&seq=craftsman-style-001&orientation=landscape',
    Tudor: 'https://readdy.ai/api/search-image?query=tudor%20style%20house%20with%20half%20timbered%20facade%20steeply%20pitched%20roof%20decorative%20woodwork%20brick%20and%20stucco%20exterior%20charming%20english%20architecture%20beautiful%20garden&width=400&height=300&seq=tudor-style-001&orientation=landscape',
    Minimalist: 'https://readdy.ai/api/search-image?query=minimalist%20modern%20house%20with%20clean%20lines%20white%20walls%20large%20windows%20simple%20geometric%20shapes%20sleek%20contemporary%20architecture%20beautiful%20minimal%20landscaping%20clear%20sky&width=400&height=300&seq=minimalist-style-001&orientation=landscape',
    Contemporary: 'https://readdy.ai/api/search-image?query=contemporary%20house%20with%20mixed%20materials%20glass%20steel%20wood%20asymmetrical%20design%20innovative%20architecture%20beautiful%20modern%20landscaping%20clear%20blue%20sky&width=400&height=300&seq=contemporary-style-001&orientation=landscape',
    Industrial: 'https://readdy.ai/api/search-image?query=industrial%20style%20house%20with%20exposed%20concrete%20metal%20accents%20large%20windows%20raw%20materials%20urban%20architecture%20beautiful%20minimal%20landscaping&width=400&height=300&seq=industrial-style-001&orientation=landscape',
    Scandinavian: 'https://readdy.ai/api/search-image?query=scandinavian%20style%20house%20with%20light%20wood%20facade%20simple%20clean%20design%20large%20windows%20natural%20materials%20nordic%20architecture%20beautiful%20minimal%20garden&width=400&height=300&seq=scandinavian-style-001&orientation=landscape',
    Mediterranean: 'https://readdy.ai/api/search-image?query=mediterranean%20villa%20with%20terracotta%20roof%20stucco%20walls%20arched%20windows%20courtyard%20warm%20earth%20tones%20beautiful%20landscaping%20palm%20trees%20clear%20sky&width=400&height=300&seq=mediterranean-style-001&orientation=landscape',
    Farmhouse: 'https://readdy.ai/api/search-image?query=modern%20farmhouse%20with%20white%20board%20and%20batten%20siding%20black%20windows%20metal%20roof%20front%20porch%20rustic%20charm%20beautiful%20landscaping%20clear%20sky&width=400&height=300&seq=farmhouse-style-001&orientation=landscape',
    Ranch: 'https://readdy.ai/api/search-image?query=ranch%20style%20house%20with%20long%20low%20profile%20attached%20garage%20horizontal%20lines%20simple%20design%20beautiful%20landscaping%20wide%20open%20yard%20clear%20sky&width=400&height=300&seq=ranch-style-001&orientation=landscape',
    'Cape Cod': 'https://readdy.ai/api/search-image?query=cape%20cod%20style%20house%20with%20steep%20roof%20dormer%20windows%20shingle%20siding%20symmetrical%20facade%20traditional%20new%20england%20architecture%20beautiful%20garden&width=400&height=300&seq=capecod-style-001&orientation=landscape',
    'Tiny Home': 'https://readdy.ai/api/search-image?query=modern%20tiny%20house%20with%20efficient%20design%20large%20windows%20wood%20siding%20compact%20architecture%20beautiful%20minimal%20landscaping%20clear%20sky%20sustainable%20living&width=400&height=300&seq=tinyhome-style-001&orientation=landscape',
    Container: 'https://readdy.ai/api/search-image?query=shipping%20container%20house%20with%20industrial%20design%20large%20windows%20modern%20conversion%20innovative%20architecture%20beautiful%20minimal%20landscaping%20clear%20sky&width=400&height=300&seq=container-style-001&orientation=landscape',
    'A-Frame': 'https://readdy.ai/api/search-image?query=a-frame%20cabin%20with%20steep%20triangular%20roof%20large%20front%20windows%20wood%20siding%20mountain%20retreat%20architecture%20beautiful%20natural%20landscaping%20clear%20sky&width=400&height=300&seq=aframe-style-001&orientation=landscape',
    Dome: 'https://readdy.ai/api/search-image?query=geodesic%20dome%20house%20with%20unique%20spherical%20design%20modern%20materials%20innovative%20architecture%20beautiful%20landscaping%20clear%20sky%20futuristic%20sustainable%20home&width=400&height=300&seq=dome-style-001&orientation=landscape',
  };
  return map[styleName] || 'https://readdy.ai/api/search-image?query=architectural%20blueprint%20technical%20drawing%20floor%20plan%20grid%20lines%20precise%20measurements%20professional%20design%20clean%20white%20background&width=400&height=300&seq=blueprint-default-001&orientation=landscape';
}

export default function DesignCanvas({
  isGenerating,
  progress,
  currentStep,
  hasGenerated,
  selectedStyle,
  floorPlanConfig,
  darkMode,
  onRegenerate,
  onShareDesign,
  onApplyStyle,
  onShowShortcuts,
}: DesignCanvasProps) {
  const [viewMode, setViewMode] = useState('split');
  const [compareMode, setCompareMode] = useState(false);
  const [compareStyle, setCompareStyle] = useState(ARCHITECTURAL_STYLES[0]);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [tipIndex, setTipIndex] = useState(0);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(true);
  const [recentSearch, setRecentSearch] = useState('');
  const [expandedProjectId, setExpandedProjectId] = useState<string | null>(null);
  const [hoveredStyle, setHoveredStyle] = useState<string | null>(null);
  const [favorites, setFavorites] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('masterArchitectFavorites');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const canvasRef = useRef<HTMLDivElement>(null);
  const leftPanelRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const exportPreviewRef = useRef<HTMLDivElement>(null);

  // ── Theme-aware class helpers ──
  const bg = darkMode ? 'bg-gray-900' : 'bg-gray-50';
  const cardBg = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const text = darkMode ? 'text-white' : 'text-gray-900';
  const subtext = darkMode ? 'text-gray-400' : 'text-gray-500';

  // ── Saved projects (read from localStorage) ──
  const savedProjects: Array<{ id: string; name: string; date: string; style: string; config: any; tags?: string[] }> = (() => {
    try {
      const raw = localStorage.getItem('masterArchitectProjects');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  })();

  // ── Filtered projects for the welcome banner search ──
  const filteredProjects = recentSearch.trim()
    ? savedProjects.filter(
        (p) =>
          p.name.toLowerCase().includes(recentSearch.toLowerCase()) ||
          (p.style || '').toLowerCase().includes(recentSearch.toLowerCase()) ||
          (p.tags || []).some((t) => t.toLowerCase().includes(recentSearch.toLowerCase()))
      )
    : savedProjects;

  // ── Getting-started checklist ──
  const checklist = [
    {
      label: 'Configure your floor plan',
      hint: 'Set bedrooms, bathrooms, area and extras in the Floor Plan tab.',
      icon: 'ri-layout-grid-line',
      done: floorPlanConfig.bedrooms > 0 && floorPlanConfig.area > 0,
    },
    {
      label: 'Choose an architectural style',
      hint: 'Pick a style from the House Style tab.',
      icon: 'ri-home-smile-line',
      done: !!selectedStyle,
    },
    {
      label: 'Generate your design',
      hint: 'Click the Generate button to create your AI design.',
      icon: 'ri-magic-line',
      done: hasGenerated,
    },
  ];
  const checklistDone = checklist.filter((c) => c.done).length;
  const checklistPercent = Math.round((checklistDone / checklist.length) * 100);

  // ── Active style for the live preview thumbnail ──
  const activePreviewStyle = hoveredStyle || selectedStyle || 'Minimalist';

  // ── Cost breakdown calculator ──
  const calculateCostBreakdown = useCallback(() => {
    const baseRatePerSqft = 150;
    const area = Number(floorPlanConfig.area) || 2000;
    const unit = floorPlanConfig.unit || 'sqft';
    const areaSqft = unit === 'sqm' ? area * 10.764 : area;

    const baseStructure = Math.round(areaSqft * baseRatePerSqft);
    const premiumRate = STYLE_PREMIUMS[selectedStyle] ?? 0;
    const stylePremium = Math.round(baseStructure * premiumRate);

    const extrasTotal = (floorPlanConfig.extras || []).reduce((sum: number, extra: string) => {
      return sum + (EXTRAS_COST[extra] ?? 10000);
    }, 0);

    const subtotal = baseStructure + stylePremium + extrasTotal;
    const variance = 0.15;

    return {
      baseStructure,
      stylePremium,
      extras: extrasTotal,
      totalMin: Math.round(subtotal * (1 - variance)),
      totalMax: Math.round(subtotal * (1 + variance)),
    };
  }, [floorPlanConfig, selectedStyle]);

  // ── Currency formatter ──
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

  // ── Tip navigation ──
  const nextTip = () => setTipIndex((i) => (i + 1) % TIPS.length);
  const prevTip = () => setTipIndex((i) => (i - 1 + TIPS.length) % TIPS.length);

  // ── Regenerate handler ──
  const handleRegenerate = () => {
    setViewMode('split');
    if (onRegenerate) onRegenerate();
  };

  // ── Download canvas as PNG ──
  const handleDownload = async (ref: React.RefObject<HTMLDivElement | null>, filename: string) => {
    if (!ref.current) return;
    try {
      const canvas = await html2canvas(ref.current, { useCORS: true, scale: 2 });
      const link = document.createElement('a');
      link.download = `${filename}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  // ── Export PDF (quick download of the canvas) ──
  const handleExportPDF = async () => {
    if (!canvasRef.current) return;
    try {
      const canvas = await html2canvas(canvasRef.current, { useCORS: true, scale: 2 });
      const link = document.createElement('a');
      link.download = `master-architect-${selectedStyle || 'design'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('PDF export failed:', err);
    }
  };

  // ── Export Summary PDF (from the preview modal) ──
  const handleExportSummary = async () => {
    if (!exportPreviewRef.current) return;
    setIsExporting(true);
    try {
      const canvas = await html2canvas(exportPreviewRef.current, { useCORS: true, scale: 2, backgroundColor: '#ffffff' });
      const link = document.createElement('a');
      link.download = `design-summary-${selectedStyle || 'report'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      setShowExportModal(false);
    } catch (err) {
      console.error('Export summary failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  // Separate images for each view so they don't overwrite each other
  const [exteriorImage, setExteriorImage] = useState(() => getStyleImage(selectedStyle, '3d', `canvas-ext-${selectedStyle}`));
  const [interiorImage, setInteriorImage] = useState(() => getStyleImage(selectedStyle, 'interior', `canvas-int-${selectedStyle}`));
  const [aerialImage, setAerialImage] = useState(() => getStyleImage(selectedStyle, '360', `canvas-360-${selectedStyle}`));
  const [compareImage, setCompareImage] = useState(() => getStyleImage(compareStyle, '3d', `canvas-compare-${compareStyle}`));
  // Active interior room for the full interior view tab
  const [activeInteriorRoom, setActiveInteriorRoom] = useState<string>('Living Room');

  // ── Aerial view regeneration ──
  const [aerialVariantIndex, setAerialVariantIndex] = useState(1); // default: Midday
  const [isRegeneratingAerial, setIsRegeneratingAerial] = useState(false);

  // ── Material Palette ──
  const [materials, setMaterials] = useState<MaterialSelection>(DEFAULT_MATERIALS);
  const [showMaterialPanel, setShowMaterialPanel] = useState(false);

  // ── Room-click interior renders ──
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [selectedRoomLabel, setSelectedRoomLabel] = useState<string | null>(null);
  const [showRoomInterior, setShowRoomInterior] = useState(false);

  // ── Download All Views ──
  const [showDownloadAll, setShowDownloadAll] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  // ── Favorite Rooms ──
  const [favoriteRooms, setFavoriteRooms] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('masterArchitectFavoriteRooms');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [showFavoriteRoomsPanel, setShowFavoriteRoomsPanel] = useState(false);

  // ── Room Compare ──
  const [showRoomCompare, setShowRoomCompare] = useState(false);
  const [compareRoomA, setCompareRoomA] = useState('living-room');
  const [compareRoomB, setCompareRoomB] = useState('master-bedroom');

  const floorPlanRef = useRef<HTMLDivElement>(null);
  const exteriorRef = useRef<HTMLDivElement>(null);
  const interiorRef = useRef<HTMLDivElement>(null);
  const aerialRef = useRef<HTMLDivElement>(null);

  // Update images when style changes
  useEffect(() => {
    setExteriorImage(getStyleImage(selectedStyle, '3d', `canvas-ext-${selectedStyle}`));
    setInteriorImage(getStyleImage(selectedStyle, 'interior', `canvas-int-${selectedStyle}`));
    setAerialImage(getStyleImage(selectedStyle, '360', `canvas-360-${selectedStyle}`));
    setActiveInteriorRoom('Living Room');
    setAerialVariantIndex(1); // reset to Midday on style change
  }, [selectedStyle]);

  // Update compare image when compare style changes
  useEffect(() => {
    setCompareImage(getStyleImage(compareStyle, '3d', `canvas-compare-${compareStyle}`));
  }, [compareStyle]);

  // ── Load a recent project from the welcome banner ──
  const handleLoadRecentProject = (project: any) => {
    window.dispatchEvent(new CustomEvent('loadRecentProject', { detail: project }));
  };

  // ── Room interior image ──
  const getRoomInteriorImage = (roomLabel: string, style: string) => {
    const roomKey = roomLabel.toLowerCase();
    const roomPrompts: Record<string, string> = {
      'master bedroom': `luxurious master bedroom interior with ${style} style design elegant bed premium bedding natural light large windows photorealistic architectural render`,
      'bedroom': `cozy bedroom interior with ${style} style design comfortable bed natural light warm atmosphere photorealistic render`,
      'living room': `beautiful living room interior with ${style} style design comfortable sofa coffee table natural light open space photorealistic render`,
      'family room': `warm family room interior with ${style} style design comfortable seating entertainment area natural light photorealistic render`,
      'kitchen': `modern kitchen interior with ${style} style design premium appliances island countertops natural light photorealistic architectural render`,
      'en-suite': `elegant en-suite bathroom interior with ${style} style design walk-in shower freestanding tub marble tiles natural light photorealistic render`,
      'main bath': `beautiful main bathroom interior with ${style} style design clean fixtures tiles natural light photorealistic render`,
      'bathroom': `clean bathroom interior with ${style} style design modern fixtures tiles natural light photorealistic render`,
      'home office': `productive home office interior with ${style} style design desk bookshelves natural light ergonomic setup photorealistic render`,
      'gym': `modern home gym interior with ${style} style design exercise equipment rubber flooring mirrors natural light photorealistic render`,
      'foyer': `welcoming foyer entry interior with ${style} style design elegant flooring statement lighting natural light photorealistic render`,
      'dining room': `elegant dining room interior with ${style} style design dining table chairs pendant lighting natural light photorealistic render`,
    };
    const prompt = roomPrompts[roomKey] || `beautiful ${roomLabel} interior with ${style} style design elegant furnishings natural light photorealistic architectural render`;
    return `https://readdy.ai/api/search-image?query=$%7BencodeURIComponent%28prompt%29%7D&width=1200&height=700&seq=room-${roomKey.replace(/\s/g, '-')}-${style}&orientation=landscape`;
  };

  // ── Handle room click on floor plan ──
  const handleRoomClick = (roomId: string, roomLabel: string) => {
    setSelectedRoomId(roomId);
    setSelectedRoomLabel(roomLabel);
    setShowRoomInterior(true);
  };

  // ── Toggle favorite room ──
  const handleToggleFavoriteRoom = (roomId: string) => {
    setFavoriteRooms((prev) => {
      const next = prev.includes(roomId) ? prev.filter((r) => r !== roomId) : [...prev, roomId];
      localStorage.setItem('masterArchitectFavoriteRooms', JSON.stringify(next));
      return next;
    });
  };

  // ── Regenerate aerial view with next variant ──
  const handleRegenerateAerial = () => {
    setIsRegeneratingAerial(true);
    const nextIndex = (aerialVariantIndex + 1) % AERIAL_VARIANTS.length;
    setAerialVariantIndex(nextIndex);
    const variant = AERIAL_VARIANTS[nextIndex];
    const style = selectedStyle || 'Modern';
    const q = `panoramic aerial view ${style} style house beautiful landscaping ${variant.suffix} photorealistic architectural visualization`;
    const newUrl = 'https://readdy.ai/api/search-image?query=%20%20%20encodeURIComponent%28q%29%20%20%20&width=1200&height=700&seq=' + variant.seq + '-' + style.replace(/\s+/g, '-').toLowerCase() + '&orientation=landscape';
    setAerialImage(newUrl);
    setTimeout(() => setIsRegeneratingAerial(false), 1200);
  };

  // ── Open room compare ──
  const handleCompareRooms = (roomAId: string, roomBId: string) => {
    setCompareRoomA(roomAId);
    setCompareRoomB(roomBId);
    setShowRoomCompare(true);
  };

  // ── Download All Views ──
  const handleDownloadAllViews = async () => {
    setDownloadingAll(true);
    setDownloadProgress(0);

    const views = [
      { ref: floorPlanRef, name: `floor-plan-${selectedStyle || 'design'}` },
      { ref: exteriorRef, name: `3d-exterior-${selectedStyle || 'design'}` },
      { ref: interiorRef, name: `interior-${selectedStyle || 'design'}` },
      { ref: aerialRef, name: `aerial-360-${selectedStyle || 'design'}` },
    ];

    for (let i = 0; i < views.length; i++) {
      const { ref, name } = views[i];
      setDownloadProgress(Math.round(((i) / views.length) * 100));
      if (ref.current) {
        try {
          const canvas = await html2canvas(ref.current, { useCORS: true, scale: 2, backgroundColor: '#ffffff' });
          const link = document.createElement('a');
          link.download = `${name}.png`;
          link.href = canvas.toDataURL('image/png');
          link.click();
          await new Promise(r => setTimeout(r, 600));
        } catch (err) {
          console.error(`Failed to download ${name}:`, err);
        }
      }
      setDownloadProgress(Math.round(((i + 1) / views.length) * 100));
    }

    setDownloadingAll(false);
    setDownloadProgress(0);
    setShowDownloadAll(false);
  };

  // ── Favorites helpers ──
  const removeFavorite = (styleName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newFavorites = favorites.filter((s) => s !== styleName);
    setFavorites(newFavorites);
    localStorage.setItem('masterArchitectFavorites', JSON.stringify(newFavorites));
  };

  const handleApplyFavorite = (styleName: string) => {
    if (onApplyStyle) onApplyStyle(styleName);
  };

  // ── Favorites Bar sub-component ──
  const FavoritesBar = () => {
    if (favorites.length === 0) return null;
    return (
      <div className={`flex items-center gap-2 px-4 py-2 border-b overflow-x-auto ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <i className="ri-heart-fill text-rose-500 text-sm"></i>
          <span className={`text-xs font-semibold whitespace-nowrap ${subtext}`}>Favorites:</span>
        </div>
        {favorites.map((styleName) => {
          const isActive = selectedStyle === styleName;
          return (
            <button
              key={styleName}
              onClick={() => handleApplyFavorite(styleName)}
              className={`relative flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer border ${
                isActive
                  ? 'bg-teal-500 text-white border-teal-500 shadow-sm'
                  : darkMode
                  ? 'bg-gray-700 text-gray-300 border-gray-600 hover:border-teal-500 hover:text-teal-300'
                  : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-teal-400 hover:text-teal-700'
              }`}
            >
              {isActive && <i className="ri-check-line text-xs"></i>}
              {styleName}
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => removeFavorite(styleName, e)}
                onKeyDown={(e) => { if (e.key === 'Enter') removeFavorite(styleName, e as any); }}
                className={`ml-0.5 rounded-full transition-colors ${isActive ? 'text-white/70 hover:text-white' : darkMode ? 'text-gray-500 hover:text-rose-400' : 'text-gray-400 hover:text-rose-500'}`}
              >
                <i className="ri-close-line text-xs"></i>
              </span>
            </button>
          );
        })}
        <span className={`text-xs flex-shrink-0 ${subtext}`}>· Click to apply</span>
      </div>
    );
  };

  // (state and effects declared above, before handlers)

  // ── GENERATING STATE ──
  if (isGenerating) {
    return (
      <div className={`flex flex-col items-center justify-center h-full min-h-[600px] ${bg} p-8`}>
        <div className={`w-full max-w-lg ${cardBg} border rounded-2xl p-8 shadow-xl`}>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
              <i className="ri-magic-line text-2xl text-white animate-pulse"></i>
            </div>
            <div>
              <h3 className={`text-xl font-bold ${text}`}>Generating Your Design</h3>
              <p className={`text-sm ${subtext}`}>AI is crafting your architectural vision…</p>
            </div>
          </div>

          <div className={`w-full h-3 rounded-full overflow-hidden mb-3 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
            <div
              className="h-full bg-gradient-to-r from-teal-500 to-cyan-400 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mb-6">
            <p className={`text-sm font-medium ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}>{currentStep}</p>
            <span className={`text-sm font-bold ${text}`}>{progress}%</span>
          </div>

          <div className="grid grid-cols-3 gap-3">
            {['Structural Model', 'Material Textures', 'Lighting & Render'].map((step, i) => (
              <div
                key={step}
                className={`p-3 rounded-xl text-center border transition-all ${
                  progress > i * 33
                    ? darkMode ? 'border-teal-600 bg-teal-900/30' : 'border-teal-300 bg-teal-50'
                    : darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'
                }`}
              >
                <i className={`ri-check-line text-lg mb-1 block ${progress > i * 33 ? 'text-teal-500' : darkMode ? 'text-gray-600' : 'text-gray-300'}`}></i>
                <span className={`text-xs font-semibold text-center leading-tight ${text}`}>{step}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── GENERATED STATE ──
  if (hasGenerated) {
    const costBreakdown = calculateCostBreakdown();

    return (
      <div className={`flex flex-col h-full ${bg}`} ref={canvasRef}>
        {/* Favorites Bar */}
        <FavoritesBar />

        {/* Action Bar */}
        <div className={`flex items-center justify-between px-6 py-3 border-b ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className={`flex items-center gap-1 p-1 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            {VIEW_MODES.map((vm) => (
              <button
                key={vm.id}
                onClick={() => setViewMode(vm.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap cursor-pointer ${
                  viewMode === vm.id
                    ? 'bg-teal-500 text-white shadow-sm'
                    : darkMode ? 'text-gray-300 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className={vm.icon}></i>
                {vm.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            {/* Download All Views */}
            <button
              onClick={() => setShowDownloadAll(true)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap cursor-pointer bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600 shadow-sm"
            >
              <i className="ri-download-cloud-line"></i>Download All Views
            </button>
            {/* Favorite Rooms */}
            <button
              onClick={() => setShowFavoriteRoomsPanel((p) => !p)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap cursor-pointer border ${
                showFavoriteRoomsPanel
                  ? 'bg-rose-500 text-white border-rose-500'
                  : darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className={`${favoriteRooms.length > 0 ? 'ri-star-fill text-amber-400' : 'ri-star-line'}`}></i>
              Rooms
              {favoriteRooms.length > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${showFavoriteRoomsPanel ? 'bg-white/20 text-white' : 'bg-rose-500 text-white'}`}>
                  {favoriteRooms.length}
                </span>
              )}
            </button>
            {/* Material Palette toggle */}
            <button
              onClick={() => setShowMaterialPanel(p => !p)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap cursor-pointer border ${
                showMaterialPanel
                  ? 'bg-rose-500 text-white border-rose-500'
                  : darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-palette-line"></i>Materials
              <div className="flex gap-0.5 ml-1">
                <div className="w-2.5 h-2.5 rounded-full border border-white/30" style={{ backgroundColor: materials.wallColor }} />
                <div className="w-2.5 h-2.5 rounded-full border border-white/30" style={{ backgroundColor: materials.accentColor }} />
              </div>
            </button>
            {!compareMode && (
              <button
                onClick={() => setCompareMode(true)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap cursor-pointer border ${
                  darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <i className="ri-contrast-2-line"></i>Compare
              </button>
            )}
            {compareMode && (
              <button
                onClick={() => setCompareMode(false)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap cursor-pointer bg-rose-500 text-white hover:bg-rose-600"
              >
                <i className="ri-close-line"></i>Exit Compare
              </button>
            )}
            <button
              onClick={() => setShowExportModal(true)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap cursor-pointer border ${
                darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-file-text-line"></i>Export Summary
            </button>
            <button
              onClick={() => setShowPrintModal(true)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap cursor-pointer border ${
                darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-printer-line"></i>Print
            </button>
            <button
              onClick={() => handleExportPDF()}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap cursor-pointer border ${
                darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-file-pdf-line"></i>PDF
            </button>
            <button
              onClick={() => handleDownload(canvasRef, `design-${selectedStyle || 'canvas'}-${viewMode}`)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap cursor-pointer border ${
                darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-download-2-line"></i>PNG
            </button>
            <button
              onClick={onShareDesign}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap cursor-pointer border ${
                darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-share-line"></i>Share
            </button>
            <button
              onClick={handleRegenerate}
              className="px-4 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap cursor-pointer bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:from-teal-600 hover:to-cyan-600 shadow-sm"
            >
              <i className="ri-refresh-line"></i>Regenerate
            </button>
          </div>
        </div>

        {/* Canvas Area */}
        {compareMode ? (
          <div className="flex flex-1 overflow-hidden">
            {/* Left: 2D Floor Plan */}
            <div className="flex-1 flex flex-col relative" ref={leftPanelRef}>
              <div className={`flex items-center justify-between px-4 py-2 ${darkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <span className="px-3 py-1 bg-teal-500 text-white text-xs font-bold rounded-full">2D Floor Plan</span>
                <span className={`text-xs font-semibold ${subtext}`}>{floorPlanConfig.bedrooms}BR · {floorPlanConfig.bathrooms}BA</span>
                <button
                  onClick={() => handleDownload(leftPanelRef, `floorplan-2d`)}
                  className={`px-2 py-1 rounded text-xs font-medium cursor-pointer ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  <i className="ri-download-line mr-1"></i>Download
                </button>
              </div>
              <div className="flex-1 relative overflow-hidden">
                <FloorPlanSVG config={floorPlanConfig} darkMode={darkMode} />
              </div>
            </div>

            <div className={`w-1 flex flex-col items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-300'} relative`}>
              <div className={`w-6 h-10 rounded-full flex items-center justify-center ${darkMode ? 'bg-gray-600' : 'bg-gray-400'}`}>
                <i className="ri-drag-move-line text-white text-xs"></i>
              </div>
            </div>

            {/* Right: 3D Exterior */}
            <div className="flex-1 flex flex-col relative" ref={rightPanelRef}>
              <div className={`flex items-center justify-between px-4 py-2 ${darkMode ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-sm border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <span className="px-3 py-1 bg-indigo-500 text-white text-xs font-bold rounded-full">3D Exterior</span>
                <select
                  value={compareStyle}
                  onChange={(e) => setCompareStyle(e.target.value)}
                  className={`text-xs font-semibold rounded-lg px-2 py-1 border cursor-pointer ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-700'}`}
                >
                  {ARCHITECTURAL_STYLES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <button
                  onClick={() => handleDownload(rightPanelRef, `design-3d-${compareStyle}`)}
                  className={`px-2 py-1 rounded text-xs font-medium cursor-pointer ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  <i className="ri-download-line mr-1"></i>Download
                </button>
              </div>
              <div className="flex-1 relative overflow-hidden">
                <img src={compareImage} alt={compareStyle} className="w-full h-full object-cover object-top transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none"></div>
                <button
                  onClick={() => onApplyStyle && onApplyStyle(compareStyle)}
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-indigo-500 text-white text-xs font-bold rounded-lg hover:bg-indigo-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Apply This Style
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 relative overflow-hidden flex flex-col">
            {/* ── SPLIT: Design Visuals (Floor Plan + 3D Exterior + Interior) ── */}
            {viewMode === 'split' && (
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Top row: 2D Floor Plan (left) + 3D Exterior (right) */}
                <div className="flex flex-1 overflow-hidden border-b border-gray-200 dark:border-gray-700">
                  {/* 2D Floor Plan */}
                  <div className="flex-1 flex flex-col relative border-r border-gray-200 dark:border-gray-700" ref={floorPlanRef}>
                    <div className={`flex items-center gap-2 px-3 py-1.5 border-b text-xs font-bold ${darkMode ? 'bg-gray-800 border-gray-700 text-teal-400' : 'bg-white border-gray-200 text-teal-700'}`}>
                      <i className="ri-layout-grid-line"></i>
                      2D Floor Plan
                      <span className={`ml-auto text-xs font-normal ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {floorPlanConfig.bedrooms}BR · {floorPlanConfig.bathrooms}BA · {floorPlanConfig.area} {floorPlanConfig.unit}
                      </span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${darkMode ? 'bg-teal-900/40 text-teal-400' : 'bg-teal-100 text-teal-700'}`}>Click room for interior</span>
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <FloorPlanSVG
                        config={floorPlanConfig}
                        darkMode={darkMode}
                        compact
                        onRoomClick={handleRoomClick}
                        selectedRoomId={selectedRoomId || undefined}
                        favoriteRoomIds={favoriteRooms}
                        onToggleFavorite={handleToggleFavoriteRoom}
                      />
                    </div>
                  </div>

                  {/* 3D Exterior */}
                  <div className="flex-1 flex flex-col relative" ref={exteriorRef}>
                    <div className={`flex items-center gap-2 px-3 py-1.5 border-b text-xs font-bold ${darkMode ? 'bg-gray-800 border-gray-700 text-indigo-400' : 'bg-white border-gray-200 text-indigo-700'}`}>
                      <i className="ri-building-2-line"></i>
                      3D Exterior Render
                      <span className={`ml-auto px-2 py-0.5 rounded-full text-xs font-semibold ${darkMode ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>
                        {selectedStyle || 'Modern'}
                      </span>
                    </div>
                    <div className="flex-1 relative overflow-hidden">
                      <ImageWithSkeleton
                        src={exteriorImage}
                        alt={`${selectedStyle} 3D exterior`}
                        className="w-full h-full object-cover object-top"
                        darkMode={darkMode}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none"></div>
                      <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                        <div className="px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-lg text-white text-xs font-semibold flex items-center gap-1.5">
                          <i className="ri-building-2-line text-indigo-300"></i>
                          {selectedStyle || 'Modern'} Style · Exterior
                        </div>
                        <button
                          onClick={() => setViewMode('3d')}
                          className="px-2 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-semibold rounded-lg hover:bg-white/30 transition-colors cursor-pointer border border-white/30"
                        >
                          Full View
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom row: Interior View */}
                <div className="h-52 flex overflow-hidden">
                  {/* Interior */}
                  <div className="flex-1 flex flex-col relative border-r border-gray-200 dark:border-gray-700" ref={interiorRef}>
                    <div className={`flex items-center gap-2 px-3 py-1.5 border-b text-xs font-bold ${darkMode ? 'bg-gray-800 border-gray-700 text-amber-400' : 'bg-white border-gray-200 text-amber-700'}`}>
                      <i className="ri-home-smile-line"></i>
                      Interior Concept
                      <span className={`ml-auto text-xs font-normal ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {selectedRoomLabel || 'Living Room'}
                      </span>
                    </div>
                    <div className="flex-1 relative overflow-hidden">
                      <ImageWithSkeleton
                        src={selectedRoomLabel
                          ? getRoomInteriorImage(selectedRoomLabel, selectedStyle)
                          : interiorImage
                        }
                        alt={`${selectedStyle} interior`}
                        className="w-full h-full object-cover object-top"
                        darkMode={darkMode}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none"></div>
                      <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 backdrop-blur-sm rounded-lg text-white text-xs font-semibold flex items-center gap-1">
                        <i className="ri-paint-brush-line text-amber-300"></i>
                        Interior · {selectedStyle || 'Modern'}
                      </div>
                      <button
                        onClick={() => setViewMode('interior')}
                        className="absolute bottom-2 right-2 px-2 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-semibold rounded-lg hover:bg-white/30 transition-colors cursor-pointer border border-white/30"
                      >
                        Full View
                      </button>
                    </div>
                  </div>

                  {/* 360 Walkthrough preview */}
                  <div className="flex-1 flex flex-col relative" ref={aerialRef}>
                    <div className={`flex items-center gap-2 px-3 py-1.5 border-b text-xs font-bold ${darkMode ? 'bg-gray-800 border-gray-700 text-rose-400' : 'bg-white border-gray-200 text-rose-700'}`}>
                      <i className="ri-camera-3-line"></i>
                      360° Aerial View
                      <span className={`ml-1 text-xs font-normal px-1.5 py-0.5 rounded-full ${darkMode ? 'bg-rose-900/40 text-rose-300' : 'bg-rose-50 text-rose-600'}`}>
                        {AERIAL_VARIANTS[aerialVariantIndex].label}
                      </span>
                      <button
                        onClick={handleRegenerateAerial}
                        disabled={isRegeneratingAerial}
                        title="Regenerate with different angle / time of day"
                        className={`ml-auto flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border ${
                          isRegeneratingAerial
                            ? darkMode ? 'border-gray-600 text-gray-500 cursor-not-allowed' : 'border-gray-200 text-gray-300 cursor-not-allowed'
                            : darkMode ? 'border-rose-700/50 text-rose-300 hover:bg-rose-900/30' : 'border-rose-200 text-rose-600 hover:bg-rose-50'
                        }`}
                      >
                        <i className={`ri-refresh-line text-xs ${isRegeneratingAerial ? 'animate-spin' : ''}`}></i>
                        Regen
                      </button>
                    </div>
                    <div className="flex-1 relative overflow-hidden">
                      <ImageWithSkeleton
                        src={aerialImage}
                        alt={`${selectedStyle} aerial`}
                        className="w-full h-full object-cover object-top"
                        darkMode={darkMode}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none"></div>
                      <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 backdrop-blur-sm rounded-lg text-white text-xs font-semibold flex items-center gap-1">
                        <i className="ri-camera-3-line text-rose-300"></i>
                        Aerial · 360°
                      </div>
                      <button
                        onClick={() => setViewMode('360')}
                        className="absolute bottom-2 right-2 px-2 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-semibold rounded-lg hover:bg-white/30 transition-colors cursor-pointer border border-white/30"
                      >
                        Full View
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 2D Floor Plan view */}
            {viewMode === '2d' && (
              <div className="flex-1 overflow-hidden">
                <FloorPlanSVG config={floorPlanConfig} darkMode={darkMode} />
              </div>
            )}

            {/* 3D Exterior view */}
            {viewMode === '3d' && (
              <div className="flex-1 relative overflow-hidden">
                <ImageWithSkeleton
                  src={exteriorImage}
                  alt={selectedStyle}
                  className="w-full h-full object-cover object-top"
                  darkMode={darkMode}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none"></div>

                {/* Style badge */}
                {selectedStyle && (
                  <div className="absolute top-4 left-4 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg flex items-center gap-2">
                    <i className="ri-home-smile-line text-teal-600"></i>
                    <span className="text-sm font-bold text-gray-900">{selectedStyle} Style</span>
                  </div>
                )}

                {/* View label */}
                <div className="absolute top-4 right-4 px-3 py-1.5 bg-black/50 backdrop-blur-sm rounded-lg text-white text-xs font-semibold flex items-center gap-1.5">
                  <i className="ri-building-2-line"></i>
                  3D Exterior Render
                </div>

                {/* Bottom info bar */}
                <div className="absolute bottom-0 left-0 right-0 px-6 py-4 bg-gradient-to-t from-black/70 to-transparent flex items-end justify-between">
                  <div className="flex items-center gap-4">
                    <div className="text-white">
                      <p className="text-xs text-white/70 mb-0.5">Configuration</p>
                      <p className="text-sm font-semibold">
                        {floorPlanConfig.bedrooms} bed · {floorPlanConfig.bathrooms} bath · {floorPlanConfig.area} {floorPlanConfig.unit}
                      </p>
                    </div>
                    {floorPlanConfig.extras?.length > 0 && (
                      <div className="text-white">
                        <p className="text-xs text-white/70 mb-0.5">Extras</p>
                        <p className="text-sm font-semibold">{floorPlanConfig.extras.slice(0, 3).join(', ')}{floorPlanConfig.extras.length > 3 ? ` +${floorPlanConfig.extras.length - 3}` : ''}</p>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleDownload(canvasRef, `design-3d-${selectedStyle}`)}
                    className="px-4 py-2 bg-white/20 backdrop-blur-sm text-white text-sm font-semibold rounded-lg hover:bg-white/30 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2 border border-white/30"
                  >
                    <i className="ri-download-line"></i>Download
                  </button>
                </div>
              </div>
            )}

            {/* Interior view */}
            {viewMode === 'interior' && (
              <div className="flex-1 relative overflow-hidden">
                <ImageWithSkeleton
                  src={getRoomInteriorImage(activeInteriorRoom, selectedStyle)}
                  alt={`${selectedStyle} interior`}
                  className="w-full h-full object-cover object-top"
                  darkMode={darkMode}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none"></div>

                <div className="absolute top-4 left-4 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg flex items-center gap-2">
                  <i className="ri-home-smile-line text-indigo-600"></i>
                  <span className="text-sm font-bold text-gray-900">
                    {activeInteriorRoom} · {selectedStyle || 'Modern'}
                  </span>
                </div>

                <div className="absolute top-4 right-4 px-3 py-1.5 bg-black/50 backdrop-blur-sm rounded-lg text-white text-xs font-semibold flex items-center gap-1.5">
                  <i className="ri-paint-brush-line"></i>
                  Interior View
                </div>

                {/* Room switcher — actually changes the image */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 flex-wrap justify-center px-4">
                  {['Living Room', 'Master Bedroom', 'Kitchen', 'Dining Room', 'Bathroom', 'Home Office'].map((room) => {
                    const isActive = activeInteriorRoom === room;
                    return (
                      <button
                        key={room}
                        onClick={() => setActiveInteriorRoom(room)}
                        className={`px-3 py-1.5 backdrop-blur-sm text-xs font-bold rounded-lg transition-all whitespace-nowrap cursor-pointer border ${
                          isActive
                            ? 'bg-teal-500 text-white border-teal-400'
                            : 'bg-white/90 text-gray-900 border-white/50 hover:bg-white'
                        }`}
                      >
                        {room}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 360° Walkthrough view */}
            {viewMode === '360' && (
              <div className="flex-1 relative overflow-hidden">
                <ImageWithSkeleton
                  src={aerialImage}
                  alt={`${selectedStyle} 360`}
                  className="w-full h-full object-cover object-top"
                  darkMode={darkMode}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none"></div>

                <div className="absolute top-4 left-4 px-4 py-2 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg flex items-center gap-2">
                  <i className="ri-camera-3-line text-amber-600"></i>
                  <span className="text-sm font-bold text-gray-900">360° Walkthrough · {selectedStyle || 'Modern'}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    darkMode ? 'bg-rose-900/60 text-rose-300' : 'bg-rose-100 text-rose-700'
                  }`}>
                    {AERIAL_VARIANTS[aerialVariantIndex].label}
                  </span>
                </div>

                <div className="absolute top-4 right-4 flex items-center gap-2">
                  {/* Regenerate button */}
                  <button
                    onClick={handleRegenerateAerial}
                    disabled={isRegeneratingAerial}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap border backdrop-blur-sm ${
                      isRegeneratingAerial
                        ? 'bg-black/30 border-white/20 text-white/40 cursor-not-allowed'
                        : 'bg-black/50 border-white/30 text-white hover:bg-rose-500/70 hover:border-rose-400'
                    }`}
                  >
                    <i className={`ri-refresh-line ${isRegeneratingAerial ? 'animate-spin' : ''}`}></i>
                    {isRegeneratingAerial ? 'Generating…' : 'New Angle'}
                  </button>
                  {/* Variant picker */}
                  <div className="relative group">
                    <button className="flex items-center gap-1.5 px-3 py-1.5 bg-black/50 backdrop-blur-sm border border-white/30 rounded-lg text-white text-xs font-semibold cursor-pointer hover:bg-black/60 transition-all whitespace-nowrap">
                      <i className="ri-sun-line"></i>
                      {AERIAL_VARIANTS[aerialVariantIndex].label}
                      <i className="ri-arrow-down-s-line text-xs"></i>
                    </button>
                    {/* Dropdown */}
                    <div className="absolute top-full right-0 mt-1 w-44 rounded-xl overflow-hidden shadow-2xl border border-white/20 bg-gray-900/95 backdrop-blur-md opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-all duration-200 z-20">
                      {AERIAL_VARIANTS.map((v, i) => (
                        <button
                          key={v.seq}
                          onClick={() => {
                            setAerialVariantIndex(i);
                            setIsRegeneratingAerial(true);
                            const style = selectedStyle || 'Modern';
                            const q = `panoramic aerial view ${style} style house beautiful landscaping ${v.suffix} photorealistic architectural visualization`;
                            const newUrl = 'https://readdy.ai/api/search-image?query=%20%20%20encodeURIComponent%28q%29%20%20%20&width=1200&height=700&seq=' + v.seq + '-' + style.replace(/\s+/g, '-').toLowerCase() + '&orientation=landscape';
                            setAerialImage(newUrl);
                            setTimeout(() => setIsRegeneratingAerial(false), 1200);
                          }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-left transition-colors cursor-pointer whitespace-nowrap ${
                            aerialVariantIndex === i
                              ? 'bg-rose-500/30 text-rose-300'
                              : 'text-gray-300 hover:bg-white/10'
                          }`}
                        >
                          {aerialVariantIndex === i && <i className="ri-check-line text-rose-400 text-xs"></i>}
                          {aerialVariantIndex !== i && <span className="w-3"></span>}
                          {v.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="px-3 py-1.5 bg-black/50 backdrop-blur-sm rounded-lg text-white text-xs font-semibold flex items-center gap-1.5 border border-white/30">
                    <i className="ri-camera-3-line"></i>
                    360° View
                  </div>
                </div>

                {/* Navigation arrows */}
                <button className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors cursor-pointer border border-white/30">
                  <i className="ri-arrow-left-s-line text-xl"></i>
                </button>
                <button className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors cursor-pointer border border-white/30">
                  <i className="ri-arrow-right-s-line text-xl"></i>
                </button>

                {/* Hotspot dots */}
                {[
                  { x: '30%', y: '45%', label: 'Living Room' },
                  { x: '55%', y: '40%', label: 'Kitchen' },
                  { x: '70%', y: '55%', label: 'Master Bedroom' },
                ].map((spot) => (
                  <button
                    key={spot.label}
                    style={{ left: spot.x, top: spot.y }}
                    className="absolute -translate-x-1/2 -translate-y-1/2 group cursor-pointer"
                  >
                    <div className="w-5 h-5 bg-white rounded-full border-2 border-teal-500 animate-pulse group-hover:scale-125 transition-transform"></div>
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-2 py-1 bg-black/80 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                      {spot.label}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {showPrintModal && (
          <PrintReadyModal
            darkMode={darkMode}
            onClose={() => setShowPrintModal(false)}
            selectedStyle={selectedStyle}
            floorPlanConfig={floorPlanConfig}
          />
        )}

        {/* ── Favorite Rooms Panel ── */}
        {showFavoriteRoomsPanel && (
          <div className="absolute top-16 right-4 z-40 max-h-[calc(100%-80px)] overflow-y-auto">
            <FavoriteRoomsPanel
              darkMode={darkMode}
              favoriteRooms={favoriteRooms}
              onToggleFavorite={handleToggleFavoriteRoom}
              onViewRoom={(roomId, roomLabel) => {
                setSelectedRoomId(roomId);
                setSelectedRoomLabel(roomLabel);
                setShowRoomInterior(true);
                setShowFavoriteRoomsPanel(false);
              }}
              onCompareRooms={(a, b) => {
                handleCompareRooms(a, b);
                setShowFavoriteRoomsPanel(false);
              }}
              selectedStyle={selectedStyle}
              getRoomInteriorImage={getRoomInteriorImage}
            />
          </div>
        )}

        {/* ── Material Palette Slide-in Panel ── */}
        {showMaterialPanel && (
          <div className="absolute top-16 right-4 z-40 w-80 max-h-[calc(100%-80px)] overflow-y-auto">
            <MaterialPalette
              darkMode={darkMode}
              selectedStyle={selectedStyle}
              currentMaterials={materials}
              onMaterialChange={(m) => {
                setMaterials(m);
                // Update 3D exterior image with material context
                const matQuery = `${selectedStyle || 'modern'} style house exterior with ${m.wallMaterial} walls ${m.roofMaterial} roof photorealistic architectural render beautiful landscaping clear sky`;
                const safeSeq = `mat-${m.wallMaterial}-${m.roofMaterial}-${(selectedStyle || 'modern').replace(/\s+/g, '-')}`;
                const matUrl = 'https://readdy.ai/api/search-image?query=%20%20%20encodeURIComponent%28matQuery%29%20%20%20&width=1200&height=700&seq=' + safeSeq + '&orientation=landscape';
                setExteriorImage(matUrl);
              }}
            />
          </div>
        )}

        {/* ── Room Interior Modal ── */}
        {showRoomInterior && selectedRoomLabel && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-6">
            <div className={`w-full max-w-3xl rounded-2xl overflow-hidden shadow-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className={`flex items-center justify-between px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
                    <i className="ri-home-smile-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedRoomLabel} — Interior Render</h3>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{selectedStyle || 'Modern'} style · AI-generated concept</p>
                  </div>
                </div>
                <button onClick={() => setShowRoomInterior(false)} className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}>
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div className="relative w-full h-96 overflow-hidden">
                <ImageWithSkeleton
                  src={getRoomInteriorImage(selectedRoomLabel, selectedStyle)}
                  alt={`${selectedRoomLabel} interior`}
                  className="w-full h-full object-cover object-top"
                  darkMode={darkMode}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none pointer-events-none"></div>
                <div className="absolute bottom-4 left-4 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-lg text-white text-xs font-semibold flex items-center gap-2">
                  <i className="ri-paint-brush-line text-amber-300"></i>
                  {selectedRoomLabel} · {selectedStyle || 'Modern'} Style
                </div>
              </div>
              {/* Room switcher */}
              <div className={`px-6 py-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <p className={`text-xs font-semibold mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Switch Room</p>
                <div className="flex flex-wrap gap-2">
                  {['Living Room', 'Master Bedroom', 'Kitchen', 'Dining Room', 'Bathroom', 'Home Office', 'Foyer'].map((room) => (
                    <button
                      key={room}
                      onClick={() => { setSelectedRoomLabel(room); setSelectedRoomId(room.toLowerCase().replace(/\s/g, '-')); }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                        selectedRoomLabel === room
                          ? 'bg-amber-500 text-white'
                          : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {room}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Download All Views Modal ── */}
        {showDownloadAll && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-6">
            <div className={`w-full max-w-md rounded-2xl shadow-2xl overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className={`flex items-center justify-between px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                    <i className="ri-download-cloud-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Download All Views</h3>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Export all 4 design panels as PNG files</p>
                  </div>
                </div>
                {!downloadingAll && (
                  <button onClick={() => setShowDownloadAll(false)} className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}>
                    <i className="ri-close-line text-lg"></i>
                  </button>
                )}
              </div>
              <div className="p-6 space-y-4">
                {/* What's included */}
                <div className="space-y-2">
                  {[
                    { icon: 'ri-layout-grid-line', label: '2D Floor Plan', color: 'text-teal-500', desc: 'Architectural drawing with rooms & dimensions' },
                    { icon: 'ri-building-2-line', label: '3D Exterior Render', color: 'text-indigo-500', desc: 'Photorealistic exterior visualization' },
                    { icon: 'ri-home-smile-line', label: 'Interior Concept', color: 'text-amber-500', desc: 'Living room interior design render' },
                    { icon: 'ri-camera-3-line', label: '360° Aerial View', color: 'text-rose-500', desc: 'Panoramic overhead visualization' },
                  ].map((item, i) => (
                    <div key={item.label} className={`flex items-center gap-3 p-3 rounded-xl ${darkMode ? 'bg-gray-700/60' : 'bg-gray-50'}`}>
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${darkMode ? 'bg-gray-600' : 'bg-white border border-gray-200'}`}>
                        <i className={`${item.icon} text-sm ${item.color}`}></i>
                      </div>
                      <div className="flex-1">
                        <p className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.label}</p>
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.desc}</p>
                      </div>
                      {downloadingAll && (
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${downloadProgress > i * 25 ? 'bg-teal-500' : darkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                          {downloadProgress > i * 25
                            ? <i className="ri-check-line text-white text-xs"></i>
                            : <i className="ri-time-line text-xs text-gray-400"></i>
                          }
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Progress bar */}
                {downloadingAll && (
                  <div>
                    <div className={`w-full h-2 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div
                        className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-500"
                        style={{ width: `${downloadProgress}%` }}
                      />
                    </div>
                    <p className={`text-xs text-center mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Downloading… {downloadProgress}%
                    </p>
                  </div>
                )}

                <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'} text-center`}>
                  <i className="ri-information-line mr-1"></i>
                  Files saved as high-res PNG (2x scale) · {selectedStyle || 'Modern'} style
                </p>
              </div>
              <div className={`flex gap-3 px-6 pb-6`}>
                {!downloadingAll && (
                  <button
                    onClick={() => setShowDownloadAll(false)}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-semibold cursor-pointer ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    Cancel
                  </button>
                )}
                <button
                  onClick={handleDownloadAllViews}
                  disabled={downloadingAll}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-bold cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap transition-all ${
                    downloadingAll
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-600 hover:to-teal-600 shadow-sm'
                  }`}
                >
                  {downloadingAll ? (
                    <><i className="ri-loader-4-line animate-spin"></i>Downloading…</>
                  ) : (
                    <><i className="ri-download-cloud-line"></i>Download All 4 Views</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Room Compare Modal ── */}
        {showRoomCompare && (
          <RoomCompareModal
            darkMode={darkMode}
            initialRoomA={compareRoomA}
            initialRoomB={compareRoomB}
            selectedStyle={selectedStyle}
            getRoomInteriorImage={getRoomInteriorImage}
            onClose={() => setShowRoomCompare(false)}
          />
        )}

        {/* Export Summary Modal */}
        {showExportModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-6">
            <div className={`w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
              {/* Modal Header */}
              <div className={`flex items-center justify-between px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center">
                    <i className="ri-file-text-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Export Summary Report</h3>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Preview before downloading</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowExportModal(false)}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-colors ${
                    darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>

              {/* Preview Content */}
              <div className="max-h-[70vh] overflow-y-auto p-6">
                <div ref={exportPreviewRef} className="bg-white p-8 space-y-6">
                  {/* Header */}
                  <div className="border-b-2 border-gray-200 pb-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h1 className="text-2xl font-bold text-gray-900 mb-1">Master Architect</h1>
                        <p className="text-sm text-gray-600">Design Summary Report</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Generated</p>
                        <p className="text-sm font-semibold text-gray-900">{new Date().toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>

                  {/* Style Section */}
                  <div>
                    <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Architectural Style</h2>
                    <div className="flex items-center gap-4">
                      <div className="w-32 h-24 rounded-lg overflow-hidden border-2 border-gray-200 flex-shrink-0">
                        <img src={getStyleThumbnail(selectedStyle)} alt={selectedStyle} className="w-full h-full object-cover object-top" />
                      </div>
                      <div>
                        <p className="text-xl font-bold text-gray-900">{selectedStyle}</p>
                        <p className="text-sm text-gray-600 mt-1">Selected architectural design style</p>
                      </div>
                    </div>
                  </div>

                  {/* Floor Plan Specs */}
                  <div>
                    <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Floor Plan Specifications</h2>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-2 mb-1">
                          <i className="ri-hotel-bed-line text-teal-600"></i>
                          <span className="text-xs text-gray-600">Bedrooms</span>
                        </div>
                        <p className="text-lg font-bold text-gray-900">{floorPlanConfig.bedrooms}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-2 mb-1">
                          <i className="ri-water-flash-line text-cyan-600"></i>
                          <span className="text-xs text-gray-600">Bathrooms</span>
                        </div>
                        <p className="text-lg font-bold text-gray-900">{floorPlanConfig.bathrooms}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-2 mb-1">
                          <i className="ri-ruler-line text-indigo-600"></i>
                          <span className="text-xs text-gray-600">Total Area</span>
                        </div>
                        <p className="text-lg font-bold text-gray-900">{floorPlanConfig.area} {floorPlanConfig.unit}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-2 mb-1">
                          <i className="ri-restaurant-line text-rose-600"></i>
                          <span className="text-xs text-gray-600">Kitchen Type</span>
                        </div>
                        <p className="text-lg font-bold text-gray-900 capitalize">{floorPlanConfig.kitchenType || 'Standard'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Extras */}
                  {floorPlanConfig.extras?.length > 0 && (
                    <div>
                      <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Additional Features</h2>
                      <div className="grid grid-cols-2 gap-2">
                        {floorPlanConfig.extras.map((extra: string, idx: number) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                            <i className="ri-checkbox-circle-fill text-teal-500 text-xs"></i>
                            <span>{extra}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Cost Estimate */}
                  <div>
                    <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">Cost Estimate</h2>
                    <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg p-4 border border-teal-200">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-700">Base Structure</span>
                          <span className="font-semibold text-gray-900">{formatCurrency(costBreakdown.baseStructure)}</span>
                        </div>
                        {costBreakdown.extras > 0 && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">Extras &amp; Add-ons</span>
                            <span className="font-semibold text-gray-900">{formatCurrency(costBreakdown.extras)}</span>
                          </div>
                        )}
                        {costBreakdown.stylePremium > 0 && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">Style Premium</span>
                            <span className="font-semibold text-gray-900">{formatCurrency(costBreakdown.stylePremium)}</span>
                          </div>
                        )}
                        <div className="border-t border-teal-300 pt-2 mt-2">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-gray-900">Estimated Range</span>
                            <span className="text-lg font-bold text-teal-700">{formatCurrency(costBreakdown.totalMin)} - {formatCurrency(costBreakdown.totalMax)}</span>
                          </div>
                          <p className="text-xs text-gray-600 text-right mt-0.5">±15% variance</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">Generated by Master Architect AI</p>
                        <p className="text-xs text-gray-400">This is an AI-generated estimate. Consult professionals for accurate quotes.</p>
                      </div>
                      <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center">
                        <i className="ri-building-2-line text-white text-sm"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className={`flex items-center justify-between px-6 py-4 border-t ${darkMode ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'}`}>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <i className="ri-information-line mr-1"></i>
                  Report will be saved as PDF (A4 format)
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowExportModal(false)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer transition-colors ${
                      darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleExportSummary}
                    disabled={isExporting}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer transition-all flex items-center gap-2 whitespace-nowrap ${
                      isExporting
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:from-teal-600 hover:to-cyan-600 shadow-sm'
                    }`}
                  >
                    {isExporting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <i className="ri-download-line"></i>
                        Download PDF
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── WELCOME BANNER ──
  return (
    <div className={`flex flex-col h-full overflow-y-auto ${bg}`}>
      <div className="max-w-4xl mx-auto w-full px-6 py-8 space-y-6">

        {/* Favorites Bar in Welcome Banner */}
        {favorites.length > 0 && (
          <div className={`rounded-2xl border ${cardBg} overflow-hidden`}>
            <div className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500">
                  <i className="ri-heart-fill text-white text-lg"></i>
                </div>
                <div>
                  <p className={`text-sm font-bold ${text}`}>Your Favorite Styles</p>
                  <p className={`text-xs ${subtext}`}>Quick access to your saved styles</p>
                </div>
                <div className={`ml-auto px-3 py-1 rounded-full text-xs font-bold ${darkMode ? 'bg-teal-900/40 text-teal-300' : 'bg-teal-100 text-teal-700'}`}>
                  {favorites.length} favorite{favorites.length !== 1 ? 's' : ''}
                </div>
              </div>

              <div className="flex gap-3 overflow-x-auto pb-2">
                {favorites.map((styleName) => {
                  const isActive = selectedStyle === styleName;
                  return (
                    <button
                      key={styleName}
                      onClick={() => handleApplyFavorite(styleName)}
                      className={`relative flex-shrink-0 w-40 rounded-xl overflow-hidden border-2 transition-all duration-200 cursor-pointer ${
                        isActive
                          ? 'border-teal-500 ring-2 ring-teal-500/30 scale-105'
                          : darkMode
                          ? 'border-gray-600 hover:border-teal-500/50'
                          : 'border-gray-200 hover:border-teal-400/50'
                      }`}
                    >
                      <div className="w-full h-24">
                        <img
                          src={getStyleThumbnail(styleName)}
                          alt={styleName}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                      {/* Remove button */}
                      <button
                        onClick={(e) => removeFavorite(styleName, e)}
                        className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200 ${
                          darkMode
                            ? 'bg-gray-800/80 text-gray-400 hover:bg-red-500 hover:text-white'
                            : 'bg-white/80 text-gray-500 hover:bg-red-500 hover:text-white'
                        }`}
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>

                      <div className="absolute bottom-0 left-0 right-0 p-2">
                        <p className="text-white text-xs font-bold truncate">{styleName}</p>
                        <p className="text-white/70 text-xs">Click to apply</p>
                      </div>

                      {/* Active indicator */}
                      {isActive && (
                        <div className="absolute top-2 left-2 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center shadow-sm">
                          <i className="ri-check-line text-white text-xs"></i>
                        </div>
                      )}

                      {/* Apply button on hover */}
                      {!isActive && (
                        <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                          <span className="px-3 py-1.5 bg-teal-500 text-white text-xs font-bold rounded-lg">
                            Apply Style
                          </span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              <p className={`text-xs text-center mt-3 ${subtext}`}>
                <i className="ri-information-line mr-1"></i>
                Add more favorites from the House Style tab
              </p>
            </div>
          </div>
        )}

        {/* Hero */}
        <div className={`rounded-2xl border overflow-hidden ${cardBg}`}>
          <div className="relative h-52 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=stunning%20modern%20architectural%20visualization%20aerial%20view%20luxury%20residential%20neighborhood%20with%20diverse%20house%20styles%20beautiful%20landscaping%20clear%20sky%20professional%20render&width=1200&height=400&seq=welcome-hero-banner-001&orientation=landscape"
              alt="Architecture hero"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/35 to-transparent"></div>
            <div className="absolute inset-0 flex flex-col justify-center px-8">
              <h2 className="text-3xl font-bold text-white mb-2">Welcome to Master Architect</h2>
              <p className="text-white/80 text-sm max-w-md leading-relaxed">Design your dream home with AI-powered tools. Configure your floor plan and generate stunning visuals in seconds.</p>
              <div className="flex items-center gap-3 mt-4">
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 backdrop-blur-sm rounded-full border border-white/20">
                  <i className="ri-building-2-line text-teal-300 text-sm"></i>
                  <span className="text-white text-xs font-semibold">16 Styles</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 backdrop-blur-sm rounded-full border border-white/20">
                  <i className="ri-tools-line text-amber-300 text-sm"></i>
                  <span className="text-white text-xs font-semibold">14 Tools</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 backdrop-blur-sm rounded-full border border-white/20">
                  <i className="ri-ai-generate text-cyan-300 text-sm"></i>
                  <span className="text-white text-xs font-semibold">AI-Powered</span>
                </div>
              </div>
            </div>
          </div>

          {/* Feature Highlights */}
          <div className="p-5 border-t border-dashed border-gray-200 dark:border-gray-700">
            <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${subtext}`}>Key Capabilities</p>
            <div className="grid grid-cols-6 gap-2">
              {FEATURE_HIGHLIGHTS.map((f) => (
                <div
                  key={f.label}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-all duration-200 hover:shadow-sm cursor-default ${darkMode ? 'border-gray-700 hover:border-gray-600' : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50'}`}
                >
                  <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${f.bg}`}>
                    <i className={`${f.icon} text-lg ${f.color}`}></i>
                  </div>
                  <span className={`text-xs font-semibold text-center leading-tight ${text}`}>{f.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Getting Started Checklist ── */}
        <div className={`rounded-2xl border ${cardBg} overflow-hidden`}>
          <button
            onClick={() => setChecklistOpen((o) => !o)}
            className={`w-full flex items-center justify-between px-6 py-4 cursor-pointer transition-colors ${darkMode ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50'}`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${checklistDone === checklist.length ? 'bg-gradient-to-br from-green-500 to-emerald-500' : 'bg-gradient-to-br from-teal-500 to-cyan-500'}`}>
                <i className={`${checklistDone === checklist.length ? 'ri-checkbox-circle-fill' : 'ri-checkbox-circle-line'} text-white text-lg`}></i>
              </div>
              <div className="text-left">
                <p className={`text-sm font-bold ${text}`}>Getting Started Checklist</p>
                <p className={`text-xs ${subtext}`}>
                  {checklistDone === checklist.length
                    ? '🎉 All steps complete — ready to generate!'
                    : `${checklistDone} of ${checklist.length} steps completed`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Circular progress */}
              <div className="relative w-10 h-10">
                <svg className="w-10 h-10 -rotate-90" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="15" fill="none" stroke={darkMode ? '#374151' : '#e5e7eb'} strokeWidth="3" />
                  <circle
                    cx="18" cy="18" r="15" fill="none"
                    stroke={checklistDone === checklist.length ? '#10b981' : '#14b8a6'}
                    strokeWidth="3"
                    strokeDasharray={`${checklistPercent * 0.942} 94.2`}
                    strokeLinecap="round"
                    className="transition-all duration-500"
                  />
                </svg>
                <span className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${checklistDone === checklist.length ? 'text-green-500' : darkMode ? 'text-teal-400' : 'text-teal-600'}`}>
                  {checklistPercent}%
                </span>
              </div>
              <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${checklistOpen ? 'rotate-180' : ''} ${subtext}`}></i>
            </div>
          </button>

          {checklistOpen && (
            <div className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              <div className="px-6 py-4 space-y-3">
                {checklist.map((item, idx) => (
                  <div
                    key={item.label}
                    className={`flex items-center gap-4 p-3 rounded-xl transition-all ${
                      item.done
                        ? darkMode ? 'bg-teal-900/20 border border-teal-800/40' : 'bg-teal-50 border border-teal-100'
                        : darkMode ? 'bg-gray-700/40 border border-gray-700' : 'bg-gray-50 border border-gray-100'
                    }`}
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${
                      item.done
                        ? 'bg-teal-500 shadow-sm shadow-teal-200'
                        : darkMode ? 'bg-gray-700 border-2 border-gray-600' : 'bg-white border-2 border-gray-300'
                    }`}>
                      {item.done
                        ? <i className="ri-check-line text-white text-sm"></i>
                        : <span className={`text-xs font-bold ${subtext}`}>{idx + 1}</span>
                      }
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-semibold ${item.done ? (darkMode ? 'text-teal-300' : 'text-teal-700') : text}`}>
                        {item.label}
                      </p>
                      {!item.done && (
                        <p className={`text-xs mt-0.5 ${subtext}`}>{item.hint}</p>
                      )}
                    </div>
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white border border-gray-200'}`}>
                      <i className={`${item.icon} text-sm ${item.done ? 'text-teal-500' : subtext}`}></i>
                    </div>
                    {item.done && (
                      <span className={`text-xs font-bold px-2 py-1 rounded-full ${darkMode ? 'bg-teal-900/40 text-teal-400' : 'bg-teal-100 text-teal-700'}`}>Done ✓</span>
                    )}
                  </div>
                ))}
              </div>
              {checklistDone === checklist.length && (
                <div className={`mx-6 mb-4 p-3 rounded-xl text-center ${darkMode ? 'bg-green-900/20 border border-green-800/40' : 'bg-green-50 border border-green-100'}`}>
                  <p className={`text-sm font-semibold ${darkMode ? 'text-green-400' : 'text-green-700'}`}>
                    🎉 You&apos;re all set! Your design is ready to explore.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Style Preview Thumbnail ── */}
        {(selectedStyle || hoveredStyle) && (
          <div className={`rounded-2xl border ${cardBg} overflow-hidden`}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-dashed border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500">
                  <i className="ri-eye-line text-white text-sm"></i>
                </div>
                <div>
                  <p className={`text-sm font-bold ${text}`}>Style Preview</p>
                  <p className={`text-xs ${subtext}`}>Live preview of your selected style</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 text-xs font-bold rounded-full ${darkMode ? 'bg-teal-900/40 text-teal-300' : 'bg-teal-100 text-teal-700'}`}>
                  {activePreviewStyle}
                </span>
                {selectedStyle && (
                  <button
                    onClick={() => onApplyStyle && onApplyStyle(selectedStyle)}
                    className="px-3 py-1 text-xs font-bold rounded-full bg-teal-500 text-white hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Apply &amp; Generate
                  </button>
                )}
              </div>
            </div>
            <div className="relative w-full h-52 overflow-hidden">
              <img
                src={STYLE_PREVIEW_IMAGES[activePreviewStyle] || STYLE_PREVIEW_IMAGES['Minimalist']}
                alt={activePreviewStyle}
                className="w-full h-full object-cover object-top transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none"></div>
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                <div>
                  <p className="text-white text-lg font-bold">{activePreviewStyle} Style</p>
                  <p className="text-white/70 text-xs">Click &quot;Apply &amp; Generate&quot; to create your design</p>
                </div>
                <div className="flex gap-2">
                  {ARCHITECTURAL_STYLES.slice(0, 4).map((s) => (
                    <button
                      key={s}
                      onMouseEnter={() => setHoveredStyle(s)}
                      onMouseLeave={() => setHoveredStyle(null)}
                      onClick={() => onApplyStyle && onApplyStyle(s)}
                      className={`w-8 h-8 rounded-lg overflow-hidden border-2 transition-all cursor-pointer flex-shrink-0 ${
                        (hoveredStyle || selectedStyle) === s ? 'border-teal-400 scale-110' : 'border-white/40 hover:border-white/80'
                      }`}
                    >
                      <img src={getStyleThumbnail(s)} alt={s} className="w-full h-full object-cover object-top" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Tips Carousel ── */}
        <div className={`rounded-2xl border ${cardBg} p-5`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 to-orange-500">
              <i className="ri-lightbulb-flash-line text-white text-lg"></i>
            </div>
            <div>
              <p className={`text-sm font-bold ${text}`}>Pro Tips</p>
              <p className={`text-xs ${subtext}`}>Tip {tipIndex + 1} of {TIPS.length}</p>
            </div>
          </div>
          <p className={`text-sm leading-relaxed mb-4 ${text}`}>{TIPS[tipIndex]}</p>
          <div className="flex items-center justify-between">
            <div className="flex gap-1.5">
              {TIPS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setTipIndex(i)}
                  className={`h-2 rounded-full transition-all cursor-pointer ${i === tipIndex ? 'bg-amber-500 w-5' : darkMode ? 'bg-gray-600 hover:bg-gray-500 w-2' : 'bg-gray-300 hover:bg-gray-400 w-2'}`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={prevTip} className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}>
                <i className="ri-arrow-left-s-line"></i>
              </button>
              <button onClick={nextTip} className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}>
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
          </div>
        </div>

        {/* ── Design Inspiration Gallery ── */}
        <div className={`rounded-2xl border ${cardBg} p-5`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-rose-500 to-pink-500">
                <i className="ri-gallery-line text-white text-lg"></i>
              </div>
              <div>
                <p className={`text-sm font-bold ${text}`}>Design Inspiration</p>
                <p className={`text-xs ${subtext}`}>Click any style to auto-apply it</p>
              </div>
            </div>
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
              {INSPIRATION_GALLERY.length} curated styles
            </span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {INSPIRATION_GALLERY.map((item) => (
              <button
                key={item.style}
                onClick={() => {
                  onApplyStyle && onApplyStyle(item.style);
                }}
                onMouseEnter={() => setHoveredStyle(item.style)}
                onMouseLeave={() => setHoveredStyle(null)}
                className={`relative rounded-xl overflow-hidden group cursor-pointer border-2 transition-all duration-200 ${
                  selectedStyle === item.style
                    ? 'border-teal-500 ring-2 ring-teal-500/30'
                    : 'border-transparent hover:border-teal-400'
                }`}
              >
                <div className="w-full h-32 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.style}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent"></div>

                {/* Tag badge */}
                <div className="absolute top-2 left-2">
                  <span className={`px-2 py-0.5 text-xs font-bold rounded-full bg-gradient-to-r ${item.accent} text-white shadow-sm`}>
                    {item.tag}
                  </span>
                </div>

                {/* Selected checkmark */}
                {selectedStyle === item.style && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center shadow-sm">
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                )}

                {/* Hover Apply button */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  {selectedStyle !== item.style && (
                    <span className="px-2 py-1 bg-white text-gray-900 text-xs font-bold rounded-lg shadow-md">Apply</span>
                  )}
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-2.5 text-left">
                  <p className="text-white text-xs font-bold leading-tight">{item.style}</p>
                  <p className="text-white/70 text-xs leading-tight">{item.label}</p>
                </div>
              </button>
            ))}
          </div>
          <p className={`text-xs text-center mt-3 ${subtext}`}>
            Hover to preview · Click to apply · Style preview updates live above
          </p>
        </div>

        {/* ── Recent Projects ── */}
        <div className={`rounded-2xl border ${cardBg} p-5`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500">
                <i className="ri-folder-open-line text-white text-lg"></i>
              </div>
              <div>
                <p className={`text-sm font-bold ${text}`}>Recent Projects</p>
                <p className={`text-xs ${subtext}`}>{savedProjects.length} saved project{savedProjects.length !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </div>

          {/* Search / Filter Bar */}
          {savedProjects.length > 0 && (
            <div className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border mb-4 transition-colors ${
              darkMode ? 'bg-gray-700/60 border-gray-600 focus-within:border-teal-600' : 'bg-gray-50 border-gray-200 focus-within:border-teal-400'
            }`}>
              <i className={`ri-search-line text-sm ${subtext}`}></i>
              <input
                type="text"
                value={recentSearch}
                onChange={(e) => setRecentSearch(e.target.value)}
                placeholder="Search by name, style, or tag…"
                className={`flex-1 bg-transparent text-sm outline-none ${text} placeholder-gray-400`}
              />
              {recentSearch ? (
                <button onClick={() => setRecentSearch('')} className={`cursor-pointer ${subtext} hover:text-rose-400 transition-colors`}>
                  <i className="ri-close-line text-sm"></i>
                </button>
              ) : (
                <i className={`ri-filter-3-line text-sm ${subtext}`}></i>
              )}
            </div>
          )}

          {filteredProjects.length === 0 ? (
            <div className={`text-center py-10 rounded-xl border-2 border-dashed ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <i className={`ri-folder-open-line text-3xl mb-2 block ${subtext}`}></i>
              <p className={`text-sm font-medium ${subtext}`}>
                {savedProjects.length === 0 ? 'No saved projects yet' : 'No projects match your search'}
              </p>
              <p className={`text-xs mt-1 ${subtext}`}>
                {savedProjects.length === 0 ? 'Save your first design to see it here' : 'Try a different search term'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredProjects.map((project) => {
                const isExpanded = expandedProjectId === project.id;
                return (
                  <div
                    key={project.id}
                    className={`rounded-xl border overflow-hidden transition-all duration-200 ${
                      darkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'
                    } ${isExpanded ? 'shadow-md' : ''}`}
                  >
                    {/* Project Row */}
                    <div className="flex items-center gap-3 p-3">
                      <div className="w-16 h-12 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200 dark:border-gray-700">
                        <img src={getStyleThumbnail(project.style)} alt={project.style} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-bold truncate ${text}`}>{project.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <p className={`text-xs ${subtext}`}>{project.style || 'No style'}</p>
                          <span className={`text-xs ${subtext}`}>·</span>
                          <p className={`text-xs ${subtext}`}>{new Date(project.date).toLocaleDateString()}</p>
                        </div>
                        {/* Tags */}
                        {project.tags?.length > 0 && (
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {project.tags.slice(0, 3).map((tag: string) => (
                              <span key={tag} className={`px-1.5 py-0.5 text-xs rounded-full ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'}`}>{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => setExpandedProjectId(isExpanded ? null : project.id)}
                          title="What's inside"
                          className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-all border ${
                            isExpanded
                              ? 'bg-teal-500 border-teal-500 text-white'
                              : darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 border-gray-600' : 'bg-white hover:bg-gray-100 text-gray-600 border-gray-200'
                          }`}
                        >
                          <i className={`${isExpanded ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                        </button>
                        <button
                          onClick={() => handleLoadRecentProject(project)}
                          className="px-3 py-1.5 bg-teal-500 hover:bg-teal-600 text-white text-xs font-bold rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                        >
                          Resume
                        </button>
                      </div>
                    </div>

                    {/* ── What's Inside Expandable ── */}
                    {isExpanded && (
                      <div className={`border-t ${darkMode ? 'border-gray-700 bg-gray-800/80' : 'border-gray-100 bg-white'} px-4 pb-4`}>
                        <div className="flex items-center gap-2 pt-3 mb-3">
                          <i className={`ri-information-line text-sm ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}></i>
                          <p className={`text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}>What&apos;s Inside</p>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            {
                              icon: 'ri-hotel-bed-line',
                              label: 'Bedrooms',
                              value: project.config?.floorPlanConfig?.bedrooms ?? '—',
                              color: 'text-indigo-500',
                              bg: darkMode ? 'bg-indigo-900/20' : 'bg-indigo-50',
                            },
                            {
                              icon: 'ri-water-flash-line',
                              label: 'Bathrooms',
                              value: project.config?.floorPlanConfig?.bathrooms ?? '—',
                              color: 'text-cyan-500',
                              bg: darkMode ? 'bg-cyan-900/20' : 'bg-cyan-50',
                            },
                            {
                              icon: 'ri-ruler-line',
                              label: 'Area',
                              value: project.config?.floorPlanConfig
                                ? `${project.config.floorPlanConfig.area} ${project.config.floorPlanConfig.unit}`
                                : '—',
                              color: 'text-teal-500',
                              bg: darkMode ? 'bg-teal-900/20' : 'bg-teal-50',
                            },
                            {
                              icon: 'ri-add-circle-line',
                              label: 'Extras',
                              value: project.config?.floorPlanConfig?.extras?.length
                                ? project.config.floorPlanConfig.extras.slice(0, 2).join(', ') +
                                  (project.config.floorPlanConfig.extras.length > 2
                                    ? ` +${project.config.floorPlanConfig.extras.length - 2}`
                                    : '')
                                : 'None',
                              color: 'text-amber-500',
                              bg: darkMode ? 'bg-amber-900/20' : 'bg-amber-50',
                            },
                          ].map((item) => (
                            <div key={item.label} className={`flex items-center gap-3 p-3 rounded-xl ${item.bg}`}>
                              <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-white'} shadow-sm`}>
                                <i className={`${item.icon} text-sm ${item.color}`}></i>
                              </div>
                              <div>
                                <p className={`text-xs ${subtext}`}>{item.label}</p>
                                <p className={`text-sm font-bold ${text}`}>{item.value}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                        {/* Kitchen type */}
                        {project.config?.floorPlanConfig?.kitchenType && (
                          <div className={`mt-2 flex items-center gap-2 px-3 py-2 rounded-xl ${darkMode ? 'bg-gray-700/60' : 'bg-gray-50'}`}>
                            <i className="ri-restaurant-line text-sm text-rose-500"></i>
                            <span className={`text-xs ${subtext}`}>Kitchen:</span>
                            <span className={`text-xs font-semibold ${text} capitalize`}>{project.config.floorPlanConfig.kitchenType}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Keyboard Shortcuts Panel ── */}
        <div className={`rounded-2xl border ${cardBg} overflow-hidden`}>
          <button
            onClick={() => setShortcutsOpen((o) => !o)}
            className={`w-full flex items-center justify-between px-6 py-4 cursor-pointer transition-colors ${darkMode ? 'hover:bg-gray-700/40' : 'hover:bg-gray-50'}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600">
                <i className="ri-keyboard-line text-white text-lg"></i>
              </div>
              <div className="text-left">
                <p className={`text-sm font-bold ${text}`}>Keyboard Shortcuts</p>
                <p className={`text-xs ${subtext}`}>Quick Reference</p>
              </div>
              <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                {SHORTCUTS_DATA.reduce((acc, cat) => acc + cat.keys.length, 0)} shortcuts
              </span>
            </div>
            <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${shortcutsOpen ? 'rotate-180' : ''} ${subtext}`}></i>
          </button>

          <div
            className="overflow-hidden transition-all duration-300"
            style={{ maxHeight: shortcutsOpen ? '500px' : '0px' }}
          >
            <div className={`px-6 pb-5 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="grid grid-cols-2 gap-5 pt-4">
                {SHORTCUTS_DATA.map((cat) => (
                  <div key={cat.category}>
                    <div className="flex items-center gap-2 mb-3">
                      <i className={`${cat.icon} text-sm ${cat.color}`}></i>
                      <span className={`text-xs font-bold uppercase tracking-wider ${subtext}`}>{cat.category}</span>
                    </div>
                    <div className="space-y-2.5">
                      {cat.keys.map((k) => (
                        <div key={k.desc} className={`flex items-center justify-between gap-2 p-2 rounded-lg ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                          <span className={`text-xs ${text}`}>{k.desc}</span>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {k.keys.map((key, ki) => (
                              <span key={`${key}-${ki}`} className="flex items-center gap-1">
                                <kbd className={`px-1.5 py-0.5 text-xs font-mono font-bold rounded border ${darkMode ? 'bg-gray-600 border-gray-500 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}>{key}</kbd>
                                {ki < k.keys.length - 1 && <span className={`text-xs ${subtext}`}>+</span>}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <button
                onClick={onShowShortcuts}
                className="mt-5 w-full py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-indigo-500 to-violet-600 text-white hover:from-indigo-600 hover:to-violet-700 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-keyboard-line"></i>See All Shortcuts
              </button>
              <p className={`text-xs text-center mt-2 ${subtext}`}>Shortcuts only work when no input field is focused.</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
